/**
 * Wave 4 — Semantic status tones for the vendor console.
 *
 * Single source of truth for "this thing is good / needs attention / is
 * broken" colouring. Components must use these instead of hardcoded palette
 * utilities (emerald/amber/red...), so light and dark themes flip cleanly and
 * every vendor surface reads the same way.
 */

export type StatusTone = "ok" | "warn" | "danger" | "info" | "neutral";

type ToneClasses = {
  /** Accessible text colour on a paper/card surface. */
  text: string;
  /** Tinted surface (chips, banners, tiles). */
  surface: string;
  /** Border for tinted surfaces. */
  border: string;
  /** Solid fill + readable foreground (badges, dots, progress). */
  solid: string;
  /** Small dot / indicator background. */
  dot: string;
  /** Focus ring colour. */
  ring: string;
};

const TONES: Record<StatusTone, ToneClasses> = {
  ok: {
    text: "text-success-ink",
    surface: "bg-success/10",
    border: "border-success/30",
    solid: "bg-success text-success-foreground",
    dot: "bg-success",
    ring: "ring-success/40",
  },
  warn: {
    text: "text-warning-ink",
    surface: "bg-warning/10",
    border: "border-warning/35",
    solid: "bg-warning text-warning-foreground",
    dot: "bg-warning",
    ring: "ring-warning/40",
  },
  danger: {
    text: "text-destructive-ink",
    surface: "bg-destructive/10",
    border: "border-destructive/30",
    solid: "bg-destructive text-destructive-foreground",
    dot: "bg-destructive",
    ring: "ring-destructive/40",
  },
  info: {
    text: "text-info-ink",
    surface: "bg-info/10",
    border: "border-info/30",
    solid: "bg-info text-info-foreground",
    dot: "bg-info",
    ring: "ring-info/40",
  },
  neutral: {
    text: "text-muted-foreground",
    surface: "bg-muted",
    border: "border-border",
    solid: "bg-muted text-foreground",
    dot: "bg-muted-foreground",
    ring: "ring-ring/40",
  },
};

export function toneClasses(tone: StatusTone): ToneClasses {
  return TONES[tone];
}

/** Text-only tone (KPIs, inline verdicts). */
export function toneText(tone: StatusTone): string {
  return TONES[tone].text;
}

/** Tinted chip/banner shell: surface + border + text. */
export function toneChip(tone: StatusTone): string {
  const t = TONES[tone];
  return `${t.surface} ${t.border} ${t.text}`;
}

/**
 * Maps the status strings used across vendor tables (orders, payouts,
 * products, returns, leads) to a tone, so one status never looks "green" in
 * one table and "grey" in another.
 */
const STATUS_TONE: Record<string, StatusTone> = {
  // orders
  placed: "info",
  confirmed: "info",
  packed: "info",
  handed_to_courier: "info",
  in_transit: "info",
  delivered: "ok",
  cancelled: "danger",
  returned: "warn",
  // payouts / payments
  requested: "warn",
  processing: "info",
  approved: "info",
  paid: "ok",
  verified: "ok",
  rejected: "danger",
  failed: "danger",
  refunded: "warn",
  pending: "warn",
  submitted: "info",
  // products / moderation
  draft: "neutral",
  live: "ok",
  published: "ok",
  suspended: "danger",
  hidden: "neutral",
  archived: "neutral",
  out_of_stock: "danger",
  low_stock: "warn",
  // orders (extra states)
  awaiting_payment: "warn",
  shipped: "info",
  refund_pending: "warn",
  // reviews / moderation decisions
  in_review: "info",
  changes: "warn",
  changes_requested: "warn",
  flagged: "warn",
  spam: "danger",
  removed: "danger",
  breached: "danger",
  sent: "ok",

  // leads / replies
  new: "warn",
  contacted: "ok",
  auto_declined: "neutral",
  // generic
  ok: "ok",
  active: "ok",
  open: "warn",
  closed: "neutral",
  resolved: "ok",
  error: "danger",
  warning: "warn",
};

export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  return STATUS_TONE[status.toLowerCase()] ?? "neutral";
}

/**
 * Threshold helper for numeric health signals (completeness %, SLA hit rate,
 * stock cover). `higherIsBetter` flips the comparison for things like
 * response time where a bigger number is worse.
 */
export function thresholdTone(
  value: number,
  { good, warn, higherIsBetter = true }: { good: number; warn: number; higherIsBetter?: boolean },
): StatusTone {
  if (!Number.isFinite(value)) return "neutral";
  if (higherIsBetter) {
    if (value >= good) return "ok";
    if (value >= warn) return "warn";
    return "danger";
  }
  if (value <= good) return "ok";
  if (value <= warn) return "warn";
  return "danger";
}
